import React from "react";
import PropTypes from "prop-types";
import { Map, OrderedMap } from "immutable";
import { List as AntList, Form, Card, Icon, Modal, Empty } from "antd";
import { getErrorOnField, fieldGenerator } from "utilities/form";
import { INPUT_TYPES, BANK_ACCOUNT_TYPES } from "constants";
import { LoaderSkeleton } from "components";
import { STATE_STATUS } from "constants/state-status";
import "styles/financial-analysis-table.css";
import { formatCurrency } from "utilities";
import AddAssociateBankAccountForm from "forms/add-associate-bank-account-form";
import { BankAccountRecord } from "records/company-record";

const LIMITS_FORM_FIELDS = new OrderedMap({
    account_type: {
        className: "form-field",
        type: INPUT_TYPES.SELECT,
        options: BANK_ACCOUNT_TYPES.filter(_ => [3, 4].includes(_))
            .map((value, label) => ({
                value,
                label
            }))
            .toList(),
        disabled: false,
        placeholder: "Select an A/C type"
    },
    account_holder_name: {
        className: "form-field",
        type: INPUT_TYPES.TEXT,
        placeholder: "Please enter A/C Holder Name",
        disabled: false
    },
    account_limit: {
        className: "form-field",
        type: INPUT_TYPES.NUMBER,
        placeholder: "Please enter a limit",
        disabled: false
    }
});

const ListItem = AntList.Item;
const ListItemMeta = AntList.Item.Meta;

// const BASE_OBJECT = {
//     account_type: null,
//     account_holder_name: null,
//     account_limit: null
// };

const ExistingBankLimitsForm = Form.create({
    mapPropsToFields: props =>
        props.values &&
        props.values
            .map(_ =>
                _.toSeq()
                    .toMap()
                    .map(__ =>
                        Form.createFormField({
                            value: __,
                            disabled: props.disabled
                        })
                    )
            )
            .toJS()
})(props => {
    const fieldGeneratorGenerated = fieldGenerator.bind(props.form);
    const getErrorOnFieldGenerated = getErrorOnField.bind(props.form);
    return (
        <Form className={"form"}>
            <AntList
                itemLayout={"horizontal"}
                className={"tabulated-list"}
                // bordered={true}
                header={
                    <ListItem>
                        <ListItemMeta title={"Account Type"} />
                        <div
                            className={
                                "d-flex fb-70 justify-content-space-between"
                            }
                        >
                            <div>A/C Holder Name</div>
                            <div>Limit</div>
                        </div>
                    </ListItem>
                }
            >
                {props.values &&
                    props.values
                        .map(_ => {
                            return (
                                <ListItem key={_.id}>
                                    <ListItemMeta
                                        title={!props.disabled ? fieldGeneratorGenerated(
                                            `${_.id}.account_type`,
                                            LIMITS_FORM_FIELDS.get("account_type"),
                                            getErrorOnFieldGenerated(
                                                `${_.id}.account_type`
                                            )
                                        )() : Array.from(BANK_ACCOUNT_TYPES.filter(__ => __ === _.account_type).keys())[0]}
                                    />
                                    <div
                                        className={
                                            "d-flex fb-70 justify-content-space-between"
                                        }
                                    >
                                        <div>
                                            {!props.disabled ? fieldGeneratorGenerated(
                                                `${_.id}.account_holder_name`,
                                                LIMITS_FORM_FIELDS.get(
                                                    "account_holder_name"
                                                ),
                                                getErrorOnFieldGenerated(
                                                    `${_.id}.account_holder_name`
                                                )
                                            )() : _.account_holder_name}
                                        </div>
                                        <div>
                                            {!props.disabled ? fieldGeneratorGenerated(
                                                `${_.id}.account_limit`,
                                                LIMITS_FORM_FIELDS.get(
                                                    "account_limit"
                                                ),
                                                getErrorOnFieldGenerated(
                                                    `${_.id}.account_limit`
                                                )
                                            )() : formatCurrency(_.account_limit, true)}
                                        </div>
                                    </div>
                                </ListItem>
                            );
                        })
                        .toList()
                        .toArray()}
            </AntList>
        </Form>
    );
});

class ExistingBankLimits extends React.Component {
    state = {
        modalVisible: false,
        status: this.props.status,
        bankLimits:
            this.props.bank_accounts && this.props.bank_accounts.size
                ? this.props.bank_accounts.filter(_ =>
                    [3, 4].includes(_.account_type)
                )
                : new Map({})
    };
    ref = React.createRef();
    formRef = React.createRef();
    bankAccount = null;
    toggleModal = () => {
        this.setState({
            modalVisible: !this.state.modalVisible
        });
    };
    saveBankAccount = () => {
        this.formRef.current.validateFields((err, values) => {
            if (!err) {
                this.toggleModal();
                this.props.update(this.props.companyId, [
                    new BankAccountRecord({
                        id: this.bankAccount ? this.bankAccount.id : null,
                        company: this.props.companyId,
                        ...values,
                        associate: values.associate === "company" ? null : values.associate
                    })
                ]);
                this.bankAccount = null;
            }
        });
    };
    addLimitField = () => {
        this.setState({
            modalVisible: true
        });
    };

    saveLimits = () => {
        this.ref.current.validateFields((err, values) => {
            if (!err) {
                this.props.update(
                    this.props.companyId,
                    this.state.bankLimits
                        .map(_ =>
                            _.merge({
                                account_limit: values[_.id].account_limit
                            })
                        )
                        .toList()
                );
            }
        });
    };

    componentDidUpdate() {
        if (this.props.status !== this.state.status) {
            this.setState({
                status: this.props.status,
                bankLimits:
                    this.props.bank_accounts && this.props.bank_accounts.size
                        ? this.props.bank_accounts.filter(_ =>
                            [3, 4].includes(_.account_type)
                        )
                        : new Map({})
            });
        }
    }

    isLoading = () => {
        return (
            [
                STATE_STATUS.UNFETCHED,
                STATE_STATUS.FETCHING,
                STATE_STATUS.UPDATING
            ].includes(this.state.status) || !this.state.status
        );
    };

    render() {
        return (
            <React.Fragment>
                <Modal
                    visible={this.state.modalVisible}
                    width={800}
                    maskClosable={true}
                    onCancel={this.toggleModal}
                    onOk={this.saveBankAccount}
                    okText={"Submit"}
                    title={"Add Bank Account"}
                >
                    <AddAssociateBankAccountForm
                        ref={this.formRef}
                        values={this.bankAccount}
                        associates={this.props.associates}
                    />
                </Modal>
                <Card
                    title={"Credit Limits"}
                    className={"padless-card small-card-title mb-10"}
                    extra={!this.props.isRiskApprover && this.props.isUserAllowedToEdit ? [
                        <a
                            key="add-limit"
                            onClick={this.addLimitField}
                            className="mr-5"
                            disabled={this.props.recreditViewOnlyPermission}
                        >
                            <Icon type={"plus"} />
                            Add Limit
                        </a>,
                        <a
                            key="save-limit"
                            onClick={this.saveLimits}
                            className="mr-5"
                            disabled={this.props.recreditViewOnlyPermission}
                        >
                            <Icon type={"check"} />
                            Save
                        </a>
                    ] : null}
                >
                    <LoaderSkeleton loading={this.isLoading()}>
                        {this.state.bankLimits.size > 0 ? (
                            <ExistingBankLimitsForm
                                ref={this.ref}
                                disabled={this.props.isRiskApprover}
                                addLimitField={this.addLimitField}
                                values={this.state.bankLimits}
                            />
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </LoaderSkeleton>
                </Card>
            </React.Fragment>
        );
    }
}

ExistingBankLimits.propTypes = {
    companyId: PropTypes.number,
    bank_accounts: PropTypes.object,
    associates: PropTypes.object,
    update: PropTypes.func,
    status: PropTypes.string,
    isRiskApprover: PropTypes.bool,
    isUserAllowedToEdit: PropTypes.bool,
    recreditViewOnlyPermission: PropTypes.bool,
};

export default ExistingBankLimits;
